import {
	Component,
	DestroyRef,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	OnInit,
	signal,
	ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RaceSessionService } from '../../timing-tower/race-session.service';
import { ThemeService } from '../../shared/theme.service';

import type * as am5 from '@amcharts/amcharts5';
import type * as am5xy from '@amcharts/amcharts5/xy';

type Marker = 'red' | 'green' | 'yellow' | 'pit' | 'unknown';

interface LapDatum {
	lap: number;
	time: number;
	marker: Marker;
}

interface Slot {
	lap: string;
	time?: number;
	marker?: Marker;
}

@Component({
	selector: 'lib-lap-time-chart',
	imports: [],
	templateUrl: './lap-time-chart.html',
	styleUrl: './lap-time-chart.css',
})
export class LapTimeChart implements OnInit, OnDestroy {
	private readonly raceSessionService = inject(RaceSessionService);
	private readonly themeService = inject(ThemeService);
	private readonly destroyRef = inject(DestroyRef);

	participantNumber = input<string>();
	loading = signal(true);

	@ViewChild('chartOutput', { static: true })
	private readonly chartOutput!: ElementRef<HTMLElement>;

	private readonly LAP_PITCH_PX = 16;
	private readonly BAR_WIDTH_PX = 12;

	private am5!: typeof import('@amcharts/amcharts5');
	private root?: am5.Root;
	private chart?: am5xy.XYChart;
	private xAxis?;
	private yAxis?;
	private series?: am5xy.ColumnSeries;

	private resizeObserver?: ResizeObserver;
	private allLaps: LapDatum[] = [];
	private ready = false;

	private readonly colorCache = new Map<string, am5.Color>();

	private readonly markerVars: Record<Marker, { name: string; fallback: number }> = {
		red: { name: '--accent-red', fallback: 0xe5484d },
		green: { name: '--accent-green', fallback: 0x30a46c },
		yellow: { name: '--accent-yellow', fallback: 0xf5d90a },
		pit: { name: '--accent-blue', fallback: 0x0091ff },
		unknown: { name: '--on-surface', fallback: 0x8b8b8b },
	};

	private readonly statusLabels: Record<Marker, string> = {
		red: 'Red flag',
		green: 'Clean lap',
		yellow: 'Yellow / SC',
		pit: 'Pit out lap',
		unknown: 'Unknown',
	};

	constructor() {
		effect(() => {
			void this.themeService._isDarkMode();
			if (this.ready) this.applyTheme();
		});

		this.raceSessionService.timingAnalysis$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data) => {
				const num = this.participantNumber();
				if (!data || !num || !data.laps?.[num]) return;

				const timingData = data.laps[num].laps;
				this.allLaps = Object.keys(timingData)
					.map((k) => ({
						lap: Number.parseInt(k, 10),
						time: timingData[k].time,
						marker: this.getStatus(
							timingData[k].sectors,
							Object.keys(timingData[k]).includes('pitOut'),
						),
					}))
					.sort((a, b) => a.lap - b.lap);

				if (this.ready) this.updateWindow();
				if (this.loading()) this.loading.set(false);
			});
	}

	ngOnInit(): void {
		Promise.all([import('@amcharts/amcharts5'), import('@amcharts/amcharts5/xy')]).then(
			([am5, am5xy]) => {
				this.am5 = am5;
				const root = am5.Root.new(this.chartOutput.nativeElement);
				this.root = root;

				const chart = root.container.children.push(
					am5xy.XYChart.new(root, {
						panX: false,
						panY: false,
						wheelX: 'none',
						wheelY: 'none',
					}),
				);
				this.chart = chart;

				const xRenderer = am5xy.AxisRendererX.new(root, {
					minGridDistance: 1, // don't let amCharts skip labels — we control which show
				});

				// Centre everything on the cell so the label sits under its bar.
				xRenderer.labels.template.setAll({ location: 0.5, multiLocation: 0.5 });
				xRenderer.grid.template.setAll({ location: 0.5 });

				// Show the lap number only every 5th lap.
				xRenderer.labels.template.adapters.add('text', (_, target) => {
					const lap = Number((target.dataItem?.dataContext as Slot | undefined)?.lap);
					return Number.isFinite(lap) && lap % 5 === 0 ? String(lap) : '';
				});

				// Match the gridlines to the labelled laps so it doesn't get noisy.
				xRenderer.grid.template.adapters.add('strokeOpacity', (_, target) => {
					const lap = Number((target.dataItem?.dataContext as Slot | undefined)?.lap);
					return Number.isFinite(lap) && lap % 5 === 0 ? 0.15 : 0;
				});

				const xAxis = chart.xAxes.push(
					am5xy.CategoryAxis.new(root, {
						categoryField: 'lap',
						renderer: xRenderer,
					}),
				);
				this.xAxis = xAxis;

				const yRenderer = am5xy.AxisRendererY.new(root, {});
				yRenderer.labels.template.adapters.add('text', (_, target) => {
					const ms = (target.dataItem as am5.DataItem<am5xy.IValueAxisDataItem>)?.get('value');
					return ms == null ? '' : this.formatLapTime(ms);
				});
				const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer }));
				this.yAxis = yAxis;

				const series = chart.series.push(
					am5xy.ColumnSeries.new(root, {
						name: 'Lap Time',
						xAxis,
						yAxis,
						valueYField: 'time',
						categoryXField: 'lap',
					}),
				);
				this.series = series;

				series.columns.template.setAll({
					width: this.BAR_WIDTH_PX,
					cornerRadiusTL: 2,
					cornerRadiusTR: 2,
					strokeOpacity: 0,
					fillOpacity: 1,
					tooltipText: '{categoryX}',
				});

				series.columns.template.adapters.add('fill', (_, target) => {
					const marker = (target.dataItem?.dataContext as Slot | undefined)?.marker ?? 'unknown';
					return this.markerColor(marker);
				});

				series.set('tooltip', am5.Tooltip.new(root, {}));
				series.columns.template.adapters.add('tooltipText', (_, target) => {
					const d = target.dataItem?.dataContext as Slot | undefined;
					if (!d?.time) return '';
					return `Lap ${d.lap}\n[bold]${this.formatLapTime(d.time)}[/]\n${this.statusLabels[d.marker ?? 'unknown']}`;
				});

				this.ready = true;
				this.applyTheme();
				this.updateWindow();

				this.resizeObserver = new ResizeObserver(() => this.updateWindow());
				this.resizeObserver.observe(this.chartOutput.nativeElement);
			},
		);
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
		this.root?.dispose();
	}

	private updateWindow(): void {
		if (!this.xAxis || !this.series) return;

		const px = this.chartOutput.nativeElement.clientWidth;
		const visible = px > 0 ? Math.max(1, Math.floor(px / this.LAP_PITCH_PX)) : 25;

		let slots: Slot[];
		if (this.allLaps.length >= visible) {
			slots = this.allLaps.slice(this.allLaps.length - visible).map((d) => this.toSlot(d));
		} else {
			slots = this.allLaps.map((d) => this.toSlot(d));
			const lastLap = this.allLaps.length ? this.allLaps[this.allLaps.length - 1].lap : 0;
			for (let i = 1; slots.length < visible; i++) {
				slots.push({ lap: String(lastLap + i) });
			}
		}

		this.xAxis.data.setAll(slots);
		this.series.data.setAll(slots);
	}

	private toSlot(d: LapDatum): Slot {
		return { lap: String(d.lap), time: d.time, marker: d.marker };
	}

	private applyTheme(): void {
		if (!this.am5 || !this.xAxis || !this.yAxis) return;
		this.colorCache.clear();
		const onSurface = this.resolveColor(this.cssVar('--on-surface'), 0x8b8b8b);

		for (const axis of [this.xAxis, this.yAxis]) {
			axis.get('renderer').labels.template.setAll({ fill: onSurface });
			axis.get('renderer').grid.template.setAll({ stroke: onSurface, strokeOpacity: 0.15 });
		}

		this.series?.columns.each((col) => {
			const marker = (col.dataItem?.dataContext as Slot | undefined)?.marker ?? 'unknown';
			col.set('fill', this.markerColor(marker));
		});
	}

	private cssVar(name: string): string {
		return getComputedStyle(this.chartOutput.nativeElement).getPropertyValue(name).trim();
	}

	private markerColor(marker: Marker): am5.Color {
		const { name, fallback } = this.markerVars[marker];
		return this.resolveColor(this.cssVar(name), fallback);
	}

	private resolveColor(css: string, fallbackHex: number): am5.Color {
		const key = css || `__fallback_${fallbackHex}`;
		const cached = this.colorCache.get(key);
		if (cached) return cached;

		const probe = document.createElement('span');
		probe.style.color = css;
		document.body.appendChild(probe);
		const computed = getComputedStyle(probe).color;
		document.body.removeChild(probe);

		const parts = computed.match(/[\d.]+/g)?.map(Number);
		const color =
			parts && parts.length >= 3
				? this.am5.color((parts[0] << 16) | (parts[1] << 8) | parts[2])
				: this.am5.color(fallbackHex);

		this.colorCache.set(key, color);
		return color;
	}

	private getStatus(
		sectors: {
			[number: number]: {
				flag: string;
				isValid: boolean;
				number: number;
				time: number;
				trackLimits: boolean;
			};
		},
		isPitOut: boolean,
	): Marker {
		const RED_FLAGS = ['RED'];
		const YELLOW_FLAGS = ['YELLOW', 'FULL_YELLOW', 'SAFETY_CAR', 'VIRTUAL_SAFETY_CAR', 'CODE_60'];
		const flags = Object.values(sectors).map((s) => s.flag);

		if (flags.some((f) => RED_FLAGS.includes(f))) return 'red';
		if (isPitOut) return 'pit';
		if (flags.some((f) => YELLOW_FLAGS.includes(f))) return 'yellow';
		if (flags.length > 0 && flags.every((f) => f === 'GREEN')) return 'green';
		return 'unknown';
	}

	private formatLapTime(ms: number): string {
		const minutes = Math.floor(ms / 60000);
		const seconds = Math.floor((ms % 60000) / 1000);
		const millis = ms % 1000;
		return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
	}
}
